/**
 * Command for forcing the text of a dimension (or giving it back its measured
 * value with an empty text).
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle } from "../core/oda";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";
import { setDimensionText } from "../dimensions/creation";

export interface SetDimensionTextParams {
  /** Handle of the dimension entity */
  entityHandle: string;
  /** Text before the change ("" = measured value) */
  fromText: string;
  /** Text after the change ("" = measured value) */
  toText: string;
}

export class SetDimensionTextCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: SetDimensionTextParams;
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: SetDimensionTextParams) {
    this.viewer = viewer;
    this.params = params;
  }

  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      {
        handle: this.params.entityHandle,
        changeKind: "dimensionText",
        oldValue: this.params.fromText,
        newValue: this.params.toText,
      },
    ]);
    if (this.executed) return;
    this.setText(this.params.toText);
    this.executed = true;
  }

  undo(): void {
    if (!this.executed) return;
    this.setText(this.params.fromText);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    if (this.executed) return;
    this.setText(this.params.toText);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {
    // Nothing to clean up
  }

  private setText(text: string): void {
    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;
    const entityId = findEntityByHandle(module, db, this.params.entityHandle);
    if (!entityId) return;

    setDimensionText(module, entityId, text);
    deleteObj(entityId);
  }
}
